import React, { forwardRef, useCallback, useEffect, useState, useRef, useImperativeHandle, useMemo } from 'react';
import classNames from 'classnames';
import { Text, useClasses, createUseStyles, Advice, Avatar, useTheme } from 'hoi-poi-ui';
import ScrollBar from 'react-custom-scrollbars';
import styles from "./styles";
const useStyles = createUseStyles(styles, 'MentionList');
const MENTION_LIST_ITEM_HEIGHT = 33;
const MAX_MENTION_LIST_ITEMS_VISIBLE_WITHOUT_SCROLL = 8;
const MentionList = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    texts,
    command,
    classes: classesProp,
    items = [],
    maxVisibleItems = MAX_MENTION_LIST_ITEMS_VISIBLE_WITHOUT_SCROLL,
    hideNoResultsPopover
  } = _ref;
  const classes = useClasses(useStyles, classesProp);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollBarRef = useRef();
  const theme = useTheme();
  useEffect(() => setSelectedIndex(0), [items]);
  const selectItem = useCallback(index => {
    const item = items[index];
    if (item) {
      command({
        id: item.id,
        label: item.name
      });
    }
  }, [command, items]);
  const scrollHandler = useCallback(index => {
    var _scrollBarRef$current;
    scrollBarRef === null || scrollBarRef === void 0 ? void 0 : (_scrollBarRef$current = scrollBarRef.current) === null || _scrollBarRef$current === void 0 ? void 0 : _scrollBarRef$current.scrollTop(index * MENTION_LIST_ITEM_HEIGHT);
  }, []);
  const upHandler = useCallback(() => {
    const newIndex = (selectedIndex + items.length - 1) % items.length;
    setSelectedIndex(newIndex);
    scrollHandler(newIndex);
  }, [items, selectedIndex, scrollHandler]);
  const downHandler = useCallback(() => {
    const newIndex = (selectedIndex + 1) % items.length;
    setSelectedIndex(newIndex);
    scrollHandler(newIndex);
  }, [items, selectedIndex, scrollHandler]);
  const enterHandler = useCallback(() => {
    selectItem(selectedIndex);
  }, [selectItem, selectedIndex]);
  useImperativeHandle(ref, () => ({
    onKeyDown: _ref2 => {
      let {
        event
      } = _ref2;
      switch (event.key) {
        case 'ArrowUp':
          upHandler();
          return true;
        case 'ArrowDown':
          downHandler();
          return true;
        case 'Enter':
        case 'Tab':
          enterHandler();
          return true;
        default:
          return false;
      }
    }
  }));
  const scrollBarProps = useMemo(() => ({
    autoHeight: true,
    autoHeightMax: MENTION_LIST_ITEM_HEIGHT * maxVisibleItems,
    autoHide: false,
    ref: scrollBarRef
  }), [maxVisibleItems]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames(classes.root, {
      [classes.isEmpty]: items.length === 0 && hideNoResultsPopover
    })
  }, (texts === null || texts === void 0 ? void 0 : texts.advice) && /*#__PURE__*/React.createElement(Advice, {
    type: "info",
    showIcon: true,
    showCollapse: false,
    className: classes.advice
  }, /*#__PURE__*/React.createElement(Text, {
    type: "caption",
    color: theme.colors.semantic.info600
  }, texts.advice)), items.length ? /*#__PURE__*/React.createElement(ScrollBar, scrollBarProps, items.map((item, index) => /*#__PURE__*/React.createElement("div", {
    className: classNames(classes.item, {
      [classes.selected]: index === selectedIndex
    }),
    key: `suggestion-${item.id}`,
    onClick: () => selectItem(index)
  }, /*#__PURE__*/React.createElement(Avatar, {
    src: item.avatar.src,
    placeholder: item.avatar.placeholder,
    className: classes.avatar
  }), /*#__PURE__*/React.createElement(Text, {
    color: theme.colors.textLight.primary,
    type: "body",
    className: classes.name,
    isTruncated: true
  }, item.name)))) : /*#__PURE__*/React.createElement(Text, null, (texts === null || texts === void 0 ? void 0 : texts.noResults) || 'No Results'));
});
export default MentionList;